/**
 * Utility to safely access environment variables in both Vite and Jest environments
 * 
 * Note: For local development, sensitive tokens (like VITE_GITHUB_TOKEN) are loaded
 * from macOS Keychain using the `load-github-token` command for enhanced security.
 * This function handles the environment variable access once tokens are loaded.
 */

/**
 * Get environment variable that works in both Vite and Jest
 */
export function getEnvVar(key: string): string | undefined {
  // Check if we're in a test environment first
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    // In Jest environment, return mock values
    if (key === 'VITE_GITHUB_TOKEN') {
      return 'mock-github-token-for-tests';
    }
    return process.env[key];
  }
  
  // In browser environment, try to access import.meta.env safely
  try {
    // Safe dynamic access to avoid TypeScript compilation issues in Jest
    // This is safer than direct eval usage as it's isolated to this specific case
    const importMeta = (0, eval)('typeof import !== "undefined" ? import.meta : undefined');
    if (importMeta && importMeta.env) {
      return importMeta.env[key];
    }
    // Fallback for environments where import.meta is not available
    return undefined;
  } catch (error) {
    // Fallback for environments where import.meta is not available
    console.warn(`Environment variable ${key} not accessible`);
    return undefined;
  }
}
